import { useTranslation } from 'react-i18next';

import { BillingNoteItem } from '@/billing/component/BillingNoteItem';
import { useBillingNotes } from '@/billing/hooks/useBillingNotes';
import { useBillingNoteTemplates } from '@/billing/hooks/useBillingNoteTemplates';
import ErrorState from '@/common/components/ErrorState';
import { Skeleton } from '@/common/components/ui/skeleton';

interface BillingNoteHistoryProps {
  billingUuid: string;
}

export const BillingNoteHistory = ({
  billingUuid,
}: BillingNoteHistoryProps) => {
  const { t } = useTranslation();

  const {
    notes,
    refetch: refetchNotes,
    isLoading: isLoadingNotes,
    isError: isErrorNotes,
  } = useBillingNotes(billingUuid);

  const {
    refetch: refetchTemplates,
    isLoading: isLoadingTemplates,
    isError: isErrorTemplates,
  } = useBillingNoteTemplates();

  if (isLoadingNotes || isLoadingTemplates) {
    return <Skeleton role="status" className="h-24 w-full" />;
  }

  if (isErrorNotes || isErrorTemplates) {
    return (
      <ErrorState
        onRetry={() => {
          void refetchNotes();
          void refetchTemplates();
        }}
      />
    );
  }

  if (!notes?.length) {
    return (
      <div className="flex flex-col items-center gap-2 text-center mt-2">
        <h3 className="font-semibold text-destructive">
          {t('billing.notesHistory.emptyTitle')}
        </h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          {t('billing.notesHistory.emptyDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {notes.map((note) => (
        <BillingNoteItem
          key={note.uuid}
          billingUuid={billingUuid}
          note={note}
        />
      ))}
    </div>
  );
};
